"use client"

import type React from "react"
import { useState, useEffect, useCallback } from "react"
import { Card, CardBody, CardHeader, Badge, Button, Row, Col, Input, InputGroup, InputGroupText } from "reactstrap"
import { FaPlus, FaEdit, FaTrash, FaCar, FaSearch } from "react-icons/fa"
import DataTable, { type TableColumn } from "react-data-table-component"
import { ExhibitorCarService } from "../../../Service/ExhibitorCarService"
import type { ExhibitorCar, UserRoles, CanCreateResponse } from "./types"
import { toast } from "react-toastify"
import AuthService from "../../../Service/AuthService"
import Swal from "sweetalert2"

interface Props {
  onAdd: () => void
  onEdit: (item: ExhibitorCar) => void
  refreshTrigger: boolean
}

const ExhibitorCarList: React.FC<Props> = ({ onAdd, onEdit, refreshTrigger }) => {
  const [cars, setCars] = useState<ExhibitorCar[]>([])
  const [stats, setStats] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(10)

  const [userRoles, setUserRoles] = useState<UserRoles>({
    is_registrar: false,
    is_staff: false,
    is_verifier: false,
    is_admin: false,
    is_superuser: false,
  })
  const [canCreate, setCanCreate] = useState<CanCreateResponse>({ can_create: false })

  useEffect(() => {
    const decodedToken = AuthService.getDecodedToken()
    if (decodedToken) {
      setUserRoles({
        is_registrar: decodedToken.is_registrar || false,
        is_staff: decodedToken.is_staff || false,
        is_verifier: decodedToken.is_verifier || false,
        is_admin: decodedToken.is_admin || false,
        is_superuser: decodedToken.is_superuser || false,
      })
    }
  }, [])

  useEffect(() => {
    fetchData()
    checkCanCreate()
  }, [refreshTrigger, userRoles, currentPage, perPage, searchTerm])

  const fetchData = async () => {
    try {
      setLoading(true)

      if (userRoles.is_admin || userRoles.is_superuser) {
        // Admin/Superuser: server-side pagination and search for all cars
        const [allCarsResponse, statsResponse] = await Promise.all([
          ExhibitorCarService.getAll(currentPage, perPage, searchTerm),
          ExhibitorCarService.getStats(),
        ])
        setCars(allCarsResponse.results)
        setTotalRows(allCarsResponse.count)
        setStats(statsResponse)
      } else {
        // Staff and non-staff: server-side pagination and search (but for their own cars)
        const allCarsResponse = await ExhibitorCarService.getAll(
          userRoles.is_staff ? currentPage : 1, // Staff gets pagination, non-staff gets all
          userRoles.is_staff ? perPage : 1000, // Staff gets normal page size, non-staff gets large page
          searchTerm,
        )
        setCars(allCarsResponse.results)
        setTotalRows(allCarsResponse.count)
      }
    } catch (error) {
      console.error("Failed to fetch cars:", error)
      toast.error("Failed to load cars")
    } finally {
      setLoading(false)
    }
  }

  const checkCanCreate = async () => {
    try {
      const canCreateResponse = await ExhibitorCarService.canCreate()
      setCanCreate(canCreateResponse)
    } catch (error) {
      console.error("Failed to check create permission:", error)
    }
  }

  const handleDelete = async (id: number) => {
    if (!userRoles.is_admin && !userRoles.is_superuser) {
      toast.error("You don't have permission to delete cars")
      return
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    })

    if (result.isConfirmed) {
      try {
        await ExhibitorCarService.delete(id)
        await Swal.fire("Deleted!", "Car has been deleted.", "success")
        fetchData()
      } catch (error) {
        await Swal.fire("Error", "Failed to delete car.", "error")
      }
    }
  }

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerRowsChange = (newPerPage: number) => {
    setPerPage(newPerPage)
    setCurrentPage(1)
  }

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm(value)
    setCurrentPage(1) // Reset to first page when searching
  }, [])

  const canEdit = userRoles.is_admin || userRoles.is_superuser
  const canDelete = userRoles.is_admin || userRoles.is_superuser
  const showCreateButton = canCreate.can_create
  const isAdminOrSuperuser = userRoles.is_admin || userRoles.is_superuser

  // DataTable columns
  const columns: TableColumn<ExhibitorCar>[] = [
    {
      name: "Plate Number",
      selector: (row) => row.plate_number,
      sortable: true,
      cell: (row) => <strong className="text-primary">{row.plate_number}</strong>,
      width: "140px",
    },
    {
      name: "Driver Name",
      selector: (row) => row.driver_name,
      sortable: true,
      width: "150px",
    },
    {
      name: "Driver Phone",
      selector: (row) => row.driver_phone,
      sortable: true,
      width: "140px",
    },
    {
      name: "Exhibitor",
      selector: (row) => row.exhibitor_name || "",
      sortable: true,
      width: "150px",
    },
    {
      name: "Exhibition",
      selector: (row) => row.participation_exhibition || "",
      sortable: true,
      width: "180px",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <Badge color={row.status ? "success" : "secondary"} pill>
          {row.status ? "Active" : "Inactive"}
        </Badge>
      ),
      width: "100px",
    },
    {
      name: "Created At",
      selector: (row) => row.created_at,
      sortable: true,
      cell: (row) => new Date(row.created_at).toLocaleDateString(),
      width: "120px",
    },
    ...(canEdit || canDelete
      ? [
          {
            name: "Actions",
            cell: (row: ExhibitorCar) => (
              <div className="d-flex gap-2">
                {canEdit && (
                  <Button color="link" className="p-0 border-0 text-info" onClick={() => onEdit(row)} title="Edit">
                    <FaEdit size={14} />
                  </Button>
                )}
                {canDelete && (
                  <Button
                    color="link"
                    className="p-0 border-0 text-danger"
                    onClick={() => handleDelete(row.id)}
                    title="Delete"
                  >
                    <FaTrash size={14} />
                  </Button>
                )}
              </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "100px",
          } as TableColumn<ExhibitorCar>,
        ]
      : []),
  ]

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f8f9fa",
        fontWeight: "bold",
        fontSize: "14px",
        color: "#495057",
      },
    },
    rows: {
      style: {
        minHeight: "56px",
        fontSize: "14px",
      },
      stripedStyle: {
        backgroundColor: "#f8f9fa",
      },
    },
    cells: {
      style: {
        paddingLeft: "12px",
        paddingRight: "12px",
      },
    },
  }

  const noDataComponent = (message: string) => (
    <div className="text-center py-4">
      <FaCar size={48} className="text-muted mb-3" />
      <h6 className="text-muted">{message}</h6>
    </div>
  )

  // Loading component for DataTable
  const LoadingComponent = () => (
    <div className="d-flex justify-content-center align-items-center py-4">
      <div className="spinner-border text-primary" role="status">
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  )

  return (
    <Card>
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <FaCar className="me-2" />
            <h5 className="mb-0">Cars Management</h5>
          </div>
          <div className="d-flex gap-2">
            {showCreateButton && (
              <Button color="primary" size="sm" onClick={onAdd}>
                <FaPlus className="me-1" />
                Register Car
              </Button>
            )}
            {!canCreate.can_create && canCreate.reason && <Badge color="warning">{canCreate.reason}</Badge>}
          </div>
        </div>

        {/* Stats Cards for Admin/Superuser */}
        {stats && isAdminOrSuperuser && (
          <Row className="mt-3">
            <Col md="4">
              <div className="bg-primary text-white p-3 rounded text-center">
                <div>
                  <strong className="fs-4">{stats.total_cars}</strong>
                  <br />
                  <small>Total Cars</small>
                </div>
              </div>
            </Col>
            <Col md="4">
              <div className="bg-success text-white p-3 rounded text-center">
                <div>
                  <strong className="fs-4">{stats.active_cars}</strong>
                  <br />
                  <small>Active Cars</small>
                </div>
              </div>
            </Col>
            <Col md="4">
              <div className="bg-warning text-white p-3 rounded text-center">
                <div>
                  <strong className="fs-4">{stats.inactive_cars}</strong>
                  <br />
                  <small>Inactive Cars</small>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </CardHeader>

      <CardBody>
        <div className="mb-3">
          <InputGroup style={{ maxWidth: "300px" }}>
            <InputGroupText>
              <FaSearch />
            </InputGroupText>
            <Input
              type="text"
              placeholder={isAdminOrSuperuser ? "Search cars..." : "Search my cars..."}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </InputGroup>
        </div>

        <DataTable
          columns={columns}
          data={cars}
          pagination={isAdminOrSuperuser || userRoles.is_staff} // Admin, superuser, and staff get pagination
          paginationServer={isAdminOrSuperuser || userRoles.is_staff}
          paginationTotalRows={isAdminOrSuperuser || userRoles.is_staff ? totalRows : undefined}
          paginationDefaultPage={isAdminOrSuperuser || userRoles.is_staff ? currentPage : undefined}
          onChangeRowsPerPage={isAdminOrSuperuser || userRoles.is_staff ? handlePerRowsChange : undefined}
          onChangePage={isAdminOrSuperuser || userRoles.is_staff ? handlePageChange : undefined}
          progressPending={loading}
          progressComponent={<LoadingComponent />}
          customStyles={customStyles}
          striped
          highlightOnHover
          responsive
          noDataComponent={noDataComponent(isAdminOrSuperuser ? "No cars found" : "No cars registered")}
          paginationRowsPerPageOptions={isAdminOrSuperuser || userRoles.is_staff ? [10, 25, 50, 100] : undefined}
        />
      </CardBody>
    </Card>
  )
}

export default ExhibitorCarList
